import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material';
import { X as CloseIcon, Send } from 'lucide-react';
import { environment } from '../config/environment';
import { getApiHeaders } from '../utils/apiUtils';

export type SendDigitalCodeEndpoint = 'couponcodes' | 'passcodes';

interface SendDigitalCodeDialogProps {
  open: boolean;
  onClose: () => void;
  endpoint: SendDigitalCodeEndpoint;
  codeId: string | null;
  codeValue?: string | null;
  contestName?: string | null;
  currentRecipient?: string | null;
  currentSentAt?: string | null;
  onSent?: (result: { codeId: string; recipient_email: string; sent_at: string }) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Diálogo para enviar (o reenviar) un ticket digital por correo a un destinatario.
 * Pensado para venta gradual: se envía un código digital puntual y se registra a
 * quién y cuándo. Sirve tanto para coupon codes como para pass codes.
 */
const SendDigitalCodeDialog: React.FC<SendDigitalCodeDialogProps> = ({
  open,
  onClose,
  endpoint,
  codeId,
  codeValue,
  contestName,
  currentRecipient,
  currentSentAt,
  onSent
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail(currentRecipient || '');
      setName('');
      setError(null);
      setSuccess(null);
    }
  }, [open, currentRecipient, codeId]);

  const alreadySent = Boolean(currentSentAt);

  const handleSend = async () => {
    const recipient = email.trim();
    if (!EMAIL_RE.test(recipient)) {
      setError('Ingresa un correo válido.');
      return;
    }
    if (!codeId) {
      setError('No se encontró el código a enviar.');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/${endpoint}/${codeId}/send-digital`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ email: recipient, name: name.trim() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'No se pudo enviar el ticket digital.');
      }
      setSuccess(`Ticket enviado a ${recipient}.`);
      onSent?.({
        codeId,
        recipient_email: data?.recipient_email || recipient,
        sent_at: data?.sent_at || new Date().toISOString()
      });
    } catch (err: any) {
      setError(err?.message || 'No se pudo enviar el ticket digital.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Enviar ticket digital
        <IconButton onClick={onClose} disabled={sending} size="small">
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {codeValue && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>Código:</Typography>
              <Chip label={codeValue} size="small" sx={{ fontFamily: 'monospace' }} />
              {contestName && (
                <Typography variant="body2" sx={{ color: '#6b7280' }}>· {contestName}</Typography>
              )}
            </Box>
          )}

          {alreadySent && (
            <Alert severity="info">
              Ya enviado a <strong>{currentRecipient || 'destinatario'}</strong>
              {currentSentAt ? ` el ${new Date(currentSentAt).toLocaleString()}` : ''}. Puedes reenviarlo.
            </Alert>
          )}

          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            El destinatario recibirá un correo con su pase digital (QR + botón para participar gratis).
          </Typography>

          <TextField
            label="Correo del destinatario"
            placeholder="persona@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={sending}
            fullWidth
            autoFocus
          />
          <TextField
            label="Nombre (opcional)"
            placeholder="Nombre del destinatario"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={sending}
            fullWidth
          />

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>Cerrar</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !email.trim()}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
          sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#ED1790', '&:hover': { backgroundColor: '#B01F8E' } }}
        >
          {sending ? 'Enviando…' : alreadySent ? 'Reenviar' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendDigitalCodeDialog;
